import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LOGO_URL } from '@/data/mockData';
import { ArrowLeft, Loader2, Sparkles, ArrowDownLeft, ArrowUpRight, Gift, RefreshCw, Lock, Check } from 'lucide-react';

const REASON_LABEL = {
  purchase: 'Compra',
  consume: 'Consumo',
  grant: 'Crédito otorgado',
  refund: 'Reembolso',
  adjust: 'Ajuste manual',
};

const REASON_ICON = {
  purchase: ArrowDownLeft,
  grant: Gift,
  refund: RefreshCw,
  adjust: Sparkles,
  consume: ArrowUpRight,
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function CreditsView({ onClose }) {
  const { session, profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(profile?.credits ?? 0);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [preorderingId, setPreorderingId] = useState(null);
  const [preorderMsg, setPreorderMsg] = useState(null);

  const load = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const [credRes, pkgRes] = await Promise.all([
      xhrJson('GET', `${API}/api/credits/me`, null, authHeader(session)),
      xhrJson('GET', `${API}/api/credits/packages`, null, authHeader(session)),
    ]);
    if (credRes.ok) {
      setCredits(credRes.data.credits ?? 0);
      setTransactions(credRes.data.transactions || []);
      refreshProfile?.();
    } else {
      setError(credRes.data?.detail || 'No pudimos cargar tu saldo.');
    }
    if (pkgRes.ok) {
      setPackages(pkgRes.data.packages || []);
      setStripeEnabled(!!pkgRes.data.stripe_enabled);
    }
    setLoading(false);
  };

  const handlePreorder = async (pkg) => {
    if (stripeEnabled) return; // Stripe path will live here later.
    setPreorderingId(pkg.id);
    setPreorderMsg(null);
    const { ok, data } = await xhrJson(
      'POST', `${API}/api/credits/checkout`,
      { package_id: pkg.id },
      authHeader(session),
    );
    setPreorderingId(null);
    if (ok) {
      setPreorderMsg({ type: 'ok', msg: data.message || 'Pre-orden registrada.' });
    } else {
      setPreorderMsg({ type: 'err', msg: data?.detail || 'No se pudo registrar la pre-orden.' });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.access_token]);

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="credits-view">
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex justify-center pl-12 md:pl-0 pointer-events-auto">
          <div className="glass-pill flex items-center gap-2.5 px-2 py-1.5 rounded-full">
            <button data-testid="credits-back" onClick={onClose} className="p-1.5 rounded-full hover:bg-black/[0.04] transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-[13px] font-medium text-gray-900 font-['Outfit'] pr-3">Créditos</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 pt-16">
        <div className="max-w-lg mx-auto w-full px-4 sm:px-6 py-4 space-y-6">
          {/* Saldo card */}
          <div className="border border-gray-200/60 rounded-2xl p-6 bg-white/40 backdrop-blur-sm">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-5 h-5 rounded-full overflow-hidden">
                <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
              </div>
              <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Saldo actual</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span data-testid="credits-balance" className="text-5xl font-medium text-gray-900 font-['Outfit'] tabular-nums">
                {loading ? '—' : credits}
              </span>
              <span className="text-sm text-gray-400">{credits === 1 ? 'crédito' : 'créditos'}</span>
            </div>
            <p className="text-[12px] text-gray-500 mt-3 leading-relaxed">
              Cada video estándar consume 1 crédito. Tu saldo se actualiza al instante cuando un equipo de producción inicia tu proyecto o cuando recibes nuevos créditos.
            </p>
          </div>

          {/* Paquetes de compra (Stripe-ready) */}
          {packages.length > 0 && (
            <div data-testid="packages-section">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">
                  Comprar más créditos
                </span>
                {!stripeEnabled && (
                  <span data-testid="stripe-status" className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" />
                    Próximamente
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {packages.map((pkg) => {
                  const busy = preorderingId === pkg.id;
                  return (
                    <div
                      key={pkg.id}
                      data-testid={`package-${pkg.id}`}
                      className={`relative rounded-2xl p-4 border transition-all bg-white/40 backdrop-blur-sm ${
                        pkg.highlight
                          ? 'border-gray-900/40 shadow-sm'
                          : 'border-gray-200/60'
                      }`}
                    >
                      {pkg.highlight && (
                        <span className="absolute -top-2 left-4 text-[10px] uppercase tracking-[0.15em] bg-gray-900 text-white px-2 py-0.5 rounded-full">
                          Recomendado
                        </span>
                      )}
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 font-['Outfit']">{pkg.name}</span>
                        <span className="text-[11px] text-gray-400 tabular-nums">{pkg.credits} créd.</span>
                      </div>
                      {pkg.description && (
                        <span className="block text-[11px] text-gray-500 mb-2">{pkg.description}</span>
                      )}
                      <div className="text-2xl font-medium text-gray-900 font-['Outfit'] tabular-nums">
                        {(pkg.price_usd ?? pkg.price_eur) != null ? `$${pkg.price_usd ?? pkg.price_eur} USD` : '—'}
                      </div>
                      <button
                        data-testid={`buy-${pkg.id}`}
                        onClick={() => handlePreorder(pkg)}
                        disabled={busy}
                        className={`mt-3 w-full text-xs rounded-xl py-2 transition-colors ${
                          stripeEnabled
                            ? 'bg-gray-900 text-white hover:bg-gray-800'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-dashed border-gray-300'
                        } disabled:opacity-50`}
                      >
                        {busy
                          ? (<span className="flex items-center justify-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reservando…</span>)
                          : stripeEnabled ? 'Comprar' : 'Pre-ordenar'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {preorderMsg && (
                <div
                  data-testid="preorder-msg"
                  className={`mt-3 text-[12px] rounded-xl px-3 py-2 border ${
                    preorderMsg.type === 'ok'
                      ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                      : 'text-rose-700 bg-rose-50 border-rose-100'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {preorderMsg.type === 'ok' ? <Check className="w-3.5 h-3.5" /> : null}
                    {preorderMsg.msg}
                  </span>
                </div>
              )}

              {!stripeEnabled && (
                <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
                  El cobro online aún no está activo. Tu pre-orden queda registrada y un manager la convertirá en créditos manualmente. Cuando conectemos Stripe será un clic.
                </p>
              )}
            </div>
          )}

          {/* Historial */}
          <div>
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Historial</span>
              <button
                data-testid="credits-refresh"
                onClick={load}
                className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Actualizar
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10 text-gray-300">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : error ? (
              <div className="text-sm text-rose-500 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
                {error}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm border border-dashed border-gray-200 rounded-xl">
                Aún no tienes movimientos.<br />Cuando compres o uses créditos aparecerán aquí.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-200/60 rounded-2xl bg-white/40">
                {transactions.map((tx) => {
                  const Icon = REASON_ICON[tx.reason] || Sparkles;
                  const positive = tx.delta > 0;
                  return (
                    <li
                      key={tx.id}
                      data-testid={`tx-${tx.id}`}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        positive ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm text-gray-900 block truncate">
                          {REASON_LABEL[tx.reason] || tx.reason}
                        </span>
                        <span className="text-[11px] text-gray-400 block truncate">
                          {tx.notes || formatDate(tx.created_at)}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`text-sm font-medium tabular-nums ${
                          positive ? 'text-emerald-600' : 'text-gray-700'
                        }`}>
                          {positive ? '+' : ''}{tx.delta}
                        </span>
                        <span className="block text-[10px] text-gray-400 tabular-nums">
                          saldo {tx.balance_after}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="text-center text-[11px] text-gray-400">
            ¿Necesitas algo a medida? Pídelo al equipo y te activamos un crédito puntual.
          </p>
        </div>
      </ScrollArea>
    </div>
  );
}
